import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TabMenu from "./TabMenu.tsx";
import FAQEditor from "./FAQEditor.tsx";
import { RootState } from "../../../redux/store";
import {
  FAQ,
  fetchFAQs,
  deleteFAQ,
} from "../../../redux/features/faq/faqSlice.tsx";
import { useAppDispatch } from "../../../hooks/useAppDispatch.tsx";
import CommonConfirmationModal from "../../commonComponents/CommonConfirmationModal.tsx";
import { toast } from "react-toastify";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useAppSelector } from "../../../hooks/useAppSelector.tsx";

const FAQPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { faqs, isLoading, error } = useAppSelector(
    (state: RootState) => state.faqs
  );

  const [isEditing, setIsEditing] = useState(false);
  const [currentFAQ, setCurrentFAQ] = useState<FAQ | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedMainTab, setSelectedMainTab] = useState<string>("faqs");
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("en");

  const profile = useAppSelector((state) => state.profile.data);

  const hasEditPermission = useMemo(() => {
    // for non-SUB_ADMIN roles return the function
    if (profile?.data?.role !== "SUB_ADMIN") {
      return true;
    }
    // For SUB_ADMIN, checking if they have the specific permission or not
    return profile?.data?.editModule?.includes("content-management");
  }, [profile?.data?.role, profile?.data?.editModule]);

  useEffect(() => {
    dispatch(fetchFAQs({ type: 2, lang: selectedSubcategory }));
    if (error) {
      toast.error(error);
    }
  }, [dispatch, selectedSubcategory, error]);

  const handleEditFAQ = (faq: FAQ) => {
    setCurrentFAQ(faq);
    setIsEditing(true);
  };

  const handleDeleteFAQ = (id: number) => {
    setSelectedId(id);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedId === null) return;

    setDeleting(true);
    try {
      await dispatch(deleteFAQ(selectedId)).unwrap();
      toast.error("FAQ deleted successfully", {
        theme: "dark",
        icon: ({ theme, type }) => <DeleteForeverIcon />,
      });
      setModalOpen(false);

      dispatch(fetchFAQs({ type: 2, lang: selectedSubcategory }));


    } catch (error) {
      console.error("Failed to delete FAQ:", error);
    } finally {
      setDeleting(false);
      setSelectedId(null);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentFAQ(null);
  };

  const applyTextColor = (element) => {
    if (element) {
      const spans = element.getElementsByTagName("span");
      for (let i = 0; i < spans.length; i++) {
        spans[i].style.color = "#3D3A3A";
        spans[i].style.fontSize = "18px";
      }
    }
  };

  const handleSubcategoryChange = (subcategory: string) => {
    if (subcategory !== selectedSubcategory) {
      setSelectedSubcategory(subcategory);
    }
  };

  return (
    <div className="py-6 px-9">
      {/* <TabMenu selectedTab="/static-content/faqs" /> */}
      <TabMenu
        selectedTab={selectedMainTab}
        selectedSubcategory={selectedSubcategory}
        onSubcategoryChange={handleSubcategoryChange}
      />

      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {!isEditing && !isLoading && (
        <div className="border rounded-lg shadow">
          <div className="flex justify-between items-center bg-[#E3F7F9] p-4 h-16 rounded-t-lg">
            <h1 className="font-bold text-2xl text-[#004135]">FAQs</h1>
            {hasEditPermission && (
              <Button
                variant="contained"
                style={{
                  fontWeight: "700",
                  fontSize: "14px",
                  backgroundColor: "#3BC1CD",
                  color: "#FFF",
                  padding: "8px 16px",
                  borderRadius: "8px",
                }}
                onClick={() => {
                  setCurrentFAQ(null);
                  setIsEditing(true);
                }}
              >
                Add New FAQ
              </Button>
            )}
          </div>
          {faqs.map((faq, index) => (
            <Card
              key={faq.contentId}
              style={{
                padding: "16px",
                margin: "12px",
                border: "1px solid #C4C4C4",
                borderRadius: "10px",
              }}
            >
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-col justify-start">
                  <div className="flex justify-start text-xl">
                    <span
                      style={{
                        fontWeight: "bold",
                        color: "#004135",
                        marginRight: "8px",
                        minWidth: "40px",
                      }}
                    >
                      Q.{index + 1}
                    </span>
                    <strong
                      ref={(el) => applyTextColor(el)}
                      dangerouslySetInnerHTML={{ __html: faq.questionEn }}
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "20px",
                      }}
                    />
                  </div>

                  <div>
                    <p
                      ref={(el) => applyTextColor(el)}
                      style={{
                        margin: 0,
                        color: "#3D3A3A",
                        fontWeight: "400",
                        fontSize: "18px",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        WebkitLineClamp: 4,
                      }}
                      dangerouslySetInnerHTML={{ __html: faq.answerEn }}
                    />
                  </div>
                </div>
                {hasEditPermission && (
                  <div className="flex flex-col">
                    <IconButton onClick={() => handleEditFAQ(faq)}>
                      <EditIcon style={{ color: "#3BC1CD" }} />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteFAQ(faq.contentId)}>
                      <DeleteIcon style={{ color: "red" }} />
                    </IconButton>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      {isEditing && <FAQEditor faq={currentFAQ} onCancel={handleCancel}   selectedSubcategory={selectedSubcategory} 
/>}
      <CommonConfirmationModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedId(null);
        }}
        title="Confirm Deletion"
        description="Are you sure you want to delete this FAQ?"
        confirmButtonText="Yes, Delete"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default FAQPage;
